/**
 * Formulario «Agregar dispositivo» / «Editar dispositivo» (RR-04, RR-05, RR-06).
 *
 * Pide lo que el GPS no transmite: modelo, SIM, ciudad, placa y cliente. El GPS
 * solo manda su identificador y su telemetría; todo lo demás nace aquí.
 *
 * Valida con el MISMO esquema Zod que el Worker. El error sale debajo de su
 * campo y el foco va al primero, para que quien llena el formulario con teclado
 * o con lector de pantalla sepa dónde está el problema. Si el servidor responde
 * con un error —un IMEI repetido, por ejemplo— sale arriba con su mensaje.
 *
 * Al editar no se muestra el IMEI: el navegador nunca lo tiene completo (RR-07).
 * Si se deja vacío, el Worker conserva el registrado.
 *
 * Un GPS que ya transmite no se registra escribiendo su IMEI (RR-23): de «Transmitiendo
 * sin registrar» viene de la trama, y de «Registrar este equipo», del motor. El formulario
 * muestra los últimos 4 dígitos, y el protocolo de la trama queda fijo.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { RastreoVehicle } from "@core/contracts/rastreo.ts";
import {
  crearEquipoSchema,
  DOCUMENT_TYPE_LABEL,
  DOCUMENT_TYPES,
  editarEquipoSchema,
  KNOWN_GPS_MODELS,
  PROTOCOL_LABEL,
  RASTREO_PROTOCOLS,
  SIM_OPERATOR_LABEL,
  SIM_OPERATORS,
  SUGGESTED_CITIES,
  type CrearEquipoForm,
  type DocumentType,
  type EditarEquipoForm,
  type RastreoPendiente,
  type RastreoProtocol,
  type RegistroCliente,
  type RegistroEquipo,
  type SaveEquipoResponse,
  type SimOperator,
} from "@core/contracts/rastreo-registro.ts";
import { Field } from "@web/components/primitives.tsx";
import { ApiError } from "@web/lib/api-client.ts";
import { CloseIcon } from "./rastreo-icons.tsx";

type ClienteModo = "ninguno" | "existente" | "nuevo";

type ClienteState = {
  nombre: string;
  tipoDocumento: DocumentType | "";
  documento: string;
  telefono: string;
  direccion: string;
  ciudad: string;
  poliza: string;
};

type FormState = {
  nombre: string;
  identificador: string;
  modelo: string;
  protocolo: RastreoProtocol | "";
  motorDeviceId: string;
  /** La trama de la que el motor toma el IMEI (RR-23). */
  tramaRef: string;
  simNumero: string;
  simOperador: SimOperator | "";
  simIccid: string;
  ciudad: string;
  placa: string;
  instaladoEn: string;
  notas: string;
  clienteModo: ClienteModo;
  clienteId: string;
  /** Con un cliente registrado elegido: corregir sus datos en el mismo guardado. */
  corregirCliente: boolean;
  cliente: ClienteState;
};

export type DeviceFormTarget =
  | { mode: "crear"; motor?: RastreoVehicle | null; pendiente?: RastreoPendiente | null }
  | { mode: "editar"; equipo: RegistroEquipo };

/** De dónde sale el IMEI al crear: escrito, de la trama o del motor (RR-23). */
function imeiSource(target: DeviceFormTarget): "escrito" | "trama" | "motor" {
  if (target.mode !== "crear") return "escrito";
  if (target.pendiente) return "trama";
  // Un motor sin D28 no manda los últimos 4 dígitos ni pone el IMEI: se escribe como antes, y el
  // motor comprueba que sea el de ese equipo (revisión independiente, hallazgo 4).
  return target.motor && target.motor.identifierTail !== null ? "motor" : "escrito";
}

const EMPTY_CLIENTE: ClienteState = {
  nombre: "",
  tipoDocumento: "CC",
  documento: "",
  telefono: "",
  direccion: "",
  ciudad: "",
  poliza: "",
};

const clienteState = (cliente: RegistroCliente | undefined): ClienteState =>
  cliente
    ? {
        nombre: cliente.nombre,
        tipoDocumento: cliente.tipoDocumento,
        documento: cliente.documento,
        telefono: cliente.telefono,
        direccion: cliente.direccion,
        ciudad: cliente.ciudad,
        poliza: cliente.poliza ?? "",
      }
    : EMPTY_CLIENTE;

/** Los últimos 4 dígitos del IMEI que pone el motor, si se conocen. */
function imeiTail(target: DeviceFormTarget): string | null {
  if (target.mode !== "crear") return null;
  return target.pendiente?.identificadorFinal ?? target.motor?.identifierTail ?? null;
}

function imeiText(target: DeviceFormTarget): string {
  const tail = imeiTail(target);
  if (target.mode === "crear" && target.pendiente) return `Termina en ${tail} · tomado de la trama`;
  return tail ? `Termina en ${tail} · el que ya tiene el motor` : "El que ya tiene el motor";
}

/** El protocolo del motor viene como `gt06-unverified` mientras el modelo está en prueba. */
function protocolFromMotor(source: string | null | undefined): RastreoProtocol | "" {
  const base = (source ?? "").replace(/-unverified$/u, "");
  return (RASTREO_PROTOCOLS as readonly string[]).includes(base) ? (base as RastreoProtocol) : "";
}

function initialState(target: DeviceFormTarget, clientes: RegistroCliente[], today: string): FormState {
  if (target.mode === "editar") {
    const equipo = target.equipo;
    const cliente = clientes.find((item) => item.id === equipo.clienteId);
    return {
      nombre: equipo.nombre,
      identificador: "",
      modelo: equipo.modelo,
      protocolo: equipo.protocolo,
      motorDeviceId: "",
      tramaRef: "",
      simNumero: equipo.simNumero,
      simOperador: equipo.simOperador,
      simIccid: equipo.simIccid ?? "",
      ciudad: equipo.ciudad,
      placa: equipo.placa ?? "",
      instaladoEn: equipo.instaladoEn ?? "",
      notas: equipo.notas ?? "",
      clienteModo: cliente ? "existente" : "ninguno",
      clienteId: cliente?.id ?? "",
      corregirCliente: false,
      cliente: clienteState(cliente),
    };
  }
  const motor = target.motor ?? null;
  const pendiente = target.pendiente ?? null;
  return {
    nombre: motor?.name ?? "",
    identificador: "",
    modelo: motor?.model ?? "",
    // De la trama manda su protocolo; si el motor mandó uno que Drive no conoce, que lo resuelva él.
    protocolo: pendiente ? (pendiente.protocolo ?? "por_confirmar") : protocolFromMotor(motor?.sourceProtocol) || "gt06",
    motorDeviceId: motor?.deviceId ?? "",
    tramaRef: pendiente?.ref ?? "",
    simNumero: "",
    simOperador: "",
    simIccid: "",
    ciudad: "",
    placa: "",
    instaladoEn: today,
    notas: "",
    clienteModo: "nuevo",
    clienteId: "",
    corregirCliente: false,
    cliente: EMPTY_CLIENTE,
  };
}

function toPayload(state: FormState): CrearEquipoForm {
  const datos = {
    nombre: state.cliente.nombre,
    tipoDocumento: state.cliente.tipoDocumento as DocumentType,
    documento: state.cliente.documento,
    telefono: state.cliente.telefono,
    direccion: state.cliente.direccion,
    ciudad: state.cliente.ciudad,
    poliza: state.cliente.poliza,
  };
  const cliente: CrearEquipoForm["cliente"] =
    state.clienteModo === "ninguno"
      ? { modo: "ninguno" }
      : state.clienteModo === "nuevo"
        ? { modo: "nuevo", datos }
        : state.corregirCliente
          ? { modo: "editar", id: state.clienteId, datos }
          : { modo: "existente", id: state.clienteId };
  return {
    nombre: state.nombre,
    identificador: state.identificador,
    modelo: state.modelo,
    protocolo: state.protocolo as RastreoProtocol,
    // Solo al registrar un equipo que el motor ya reporta: el motor pone su IMEI. Al editar, el
    // Worker usa el vínculo guardado.
    motorDeviceId: state.motorDeviceId,
    tramaRef: state.tramaRef,
    simNumero: state.simNumero,
    simOperador: state.simOperador as SimOperator,
    simIccid: state.simIccid,
    ciudad: state.ciudad,
    placa: state.placa,
    instaladoEn: state.instaladoEn,
    notas: state.notas,
    cliente,
  };
}

/** Ruta del error en el esquema → id del campo en pantalla. */
const FIELD_ID: Record<string, string> = {
  nombre: "nombre",
  identificador: "identificador",
  modelo: "modelo",
  protocolo: "protocolo",
  // No hay campo para elegirlos: un error del equipo del motor o de la trama va al IMEI.
  motorDeviceId: "identificador",
  tramaRef: "identificador",
  simNumero: "sim-numero",
  simOperador: "sim-operador",
  simIccid: "sim-iccid",
  ciudad: "ciudad",
  placa: "placa",
  instaladoEn: "instalado",
  notas: "notas",
  "cliente.id": "cliente-id",
  "cliente.datos.nombre": "cliente-nombre",
  "cliente.datos.tipoDocumento": "cliente-tipo",
  "cliente.datos.documento": "cliente-documento",
  "cliente.datos.telefono": "cliente-telefono",
  "cliente.datos.direccion": "cliente-direccion",
  "cliente.datos.ciudad": "cliente-ciudad",
  "cliente.datos.poliza": "cliente-poliza",
};

/** El orden en que aparecen en pantalla: el foco va al primero que falle. */
const FIELD_ORDER = Object.values(FIELD_ID);

export function DeviceForm({
  target,
  clientes,
  today,
  onCancel,
  onSaved,
  save,
}: {
  target: DeviceFormTarget;
  clientes: RegistroCliente[];
  today: string;
  onCancel: () => void;
  onSaved: (result: SaveEquipoResponse) => void;
  save: (payload: CrearEquipoForm | EditarEquipoForm) => Promise<SaveEquipoResponse>;
}) {
  const uid = useId();
  const id = (field: string) => `${uid}-${field}`;
  const titleId = id("titulo");
  const editing = target.mode === "editar";
  const source = imeiSource(target);
  // El protocolo de la trama no se cambia: el motor rechazaría cualquier otro (D26).
  const protocolLocked = target.mode === "crear" && Boolean(target.pendiente?.protocolo);
  const [state, setState] = useState<FormState>(() => initialState(target, clientes, today));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const dialog = useRef<HTMLDivElement | null>(null);
  const serverAlert = useRef<HTMLParagraphElement | null>(null);

  const mutation = useMutation({ mutationFn: save, onSuccess: onSaved });

  // El rechazo del servidor sale arriba del formulario, y quien lo envió está abajo, junto al
  // botón: se lleva la vista y el foco al mensaje para que no quede escondido.
  useEffect(() => {
    if (!mutation.isError || !serverAlert.current) return;
    serverAlert.current.scrollIntoView?.({ block: "start", behavior: "smooth" });
    serverAlert.current.focus();
  }, [mutation.isError, mutation.error]);

  const sortedClients = useMemo(
    () => [...clientes].sort((a, b) => a.nombre.localeCompare(b.nombre, "es") || a.id.localeCompare(b.id)),
    [clientes],
  );

  // Foco al primer campo al abrir, y de vuelta a donde estaba al cerrar.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    dialog.current?.querySelector<HTMLElement>("input, select, textarea")?.focus();
    return () => previous?.focus?.();
  }, []);

  // Escape cierra, salvo mientras se guarda.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !mutation.isPending) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mutation.isPending, onCancel]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setState((current) => ({ ...current, [key]: value }));
  const setCliente = <K extends keyof ClienteState>(key: K, value: ClienteState[K]) =>
    setState((current) => ({ ...current, cliente: { ...current.cliente, [key]: value } }));

  const chooseClient = (clienteId: string) =>
    setState((current) => ({
      ...current,
      clienteId,
      cliente: clienteState(clientes.find((item) => item.id === clienteId)),
    }));

  const chooseModel = (modelo: string) =>
    setState((current) => {
      const known = KNOWN_GPS_MODELS.find((item) => item.code.toLowerCase() === modelo.trim().toLowerCase());
      return { ...current, modelo, protocolo: known && !protocolLocked ? known.protocol : current.protocolo };
    });

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const payload = toPayload(state);
    const parsed = (editing ? editarEquipoSchema : crearEquipoSchema).safeParse(payload);
    if (!parsed.success) {
      const found: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const field = FIELD_ID[issue.path.join(".")];
        if (field && !found[field]) found[field] = issue.message;
      }
      setErrors(found);
      const first = FIELD_ORDER.find((field) => found[field]);
      if (first) document.getElementById(id(first))?.focus();
      return;
    }
    setErrors({});
    mutation.mutate(payload);
  };

  /** Atributos de accesibilidad de un control con posible error. */
  const control = (field: string) => ({
    id: id(field),
    "aria-invalid": errors[field] ? true : undefined,
    "aria-describedby": errors[field] ? `${id(field)}-error` : undefined,
  });

  const serverError = mutation.isError
    ? mutation.error instanceof ApiError
      ? mutation.error.message
      : "No se pudo guardar. Vuelve a intentarlo."
    : null;

  const clientFieldsVisible = state.clienteModo === "nuevo" || (state.clienteModo === "existente" && state.corregirCliente);

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-gd-deep/60 sm:items-center sm:p-6">
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex w-full flex-col overflow-hidden bg-gd-mist sm:max-h-[92dvh] sm:max-w-3xl sm:rounded-[22px] sm:shadow-[0_30px_80px_rgba(7,21,33,0.45)]"
      >
        <header className="flex items-center gap-3 bg-gd-deep px-5 py-4 text-white">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="font-display text-lg font-bold text-white">
              {editing ? "Editar dispositivo" : "Agregar dispositivo"}
            </h2>
            <p className="text-[13px] text-white/70">
              El GPS solo transmite su identificador y su ubicación. Aquí va lo que no transmite.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={mutation.isPending}
            className="flex size-10 shrink-0 items-center justify-center rounded-xl text-white/80 hover:bg-white/10 hover:text-white"
          >
            <CloseIcon />
            <span className="sr-only">Cerrar sin guardar</span>
          </button>
        </header>

        <form noValidate onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
            {serverError ? (
              <p
                ref={serverAlert}
                role="alert"
                tabIndex={-1}
                className="rounded-[14px] border border-red-300 bg-red-50 px-4 py-3 text-[15px] text-red-900"
              >
                {serverError}
              </p>
            ) : null}
            {Object.keys(errors).length > 0 ? (
              <p role="alert" className="rounded-[14px] border border-amber-300 bg-amber-50 px-4 py-3 text-[15px] text-amber-950">
                Revisa los campos marcados.
              </p>
            ) : null}

            <Section title="Equipo GPS">
              <Field label="Nombre" htmlFor={id("nombre")} hint="Como se verá en la lista: la placa o una etiqueta." error={errors.nombre}>
                <input {...control("nombre")} value={state.nombre} onChange={(e) => set("nombre", e.target.value)} autoComplete="off" />
              </Field>
              {source === "escrito" ? (
                <Field
                  label={editing ? "Identificador (IMEI), solo si cambió" : "Identificador (IMEI)"}
                  htmlFor={id("identificador")}
                  hint={
                    editing && target.mode === "editar"
                      ? `Registrado: termina en ${target.equipo.identificadorFinal}. Déjalo vacío para conservarlo.`
                      : "Está en la etiqueta del equipo o se pide por SMS."
                  }
                  error={errors.identificador}
                >
                  <input
                    {...control("identificador")}
                    value={state.identificador}
                    onChange={(e) => set("identificador", e.target.value)}
                    inputMode="numeric"
                    autoComplete="off"
                  />
                </Field>
              ) : (
                <div>
                  <p className="m-0 text-[14px] font-semibold text-gd-navy">Identificador (IMEI)</p>
                  {/* Sin campo: el IMEI lo pone el motor. El foco de un error llega aquí. */}
                  <p id={id("identificador")} tabIndex={-1} className="m-0 mt-1.5 rounded-[14px] border border-gd-line bg-gd-mist px-4 py-3 text-[15px] text-gd-ink">
                    {imeiText(target)}
                  </p>
                  {errors.identificador ? (
                    <p role="alert" className="m-0 mt-1 text-[14px] text-red-700">
                      {errors.identificador}
                    </p>
                  ) : null}
                </div>
              )}
              <Field label="Modelo" htmlFor={id("modelo")} error={errors.modelo}>
                <input
                  {...control("modelo")}
                  value={state.modelo}
                  onChange={(e) => chooseModel(e.target.value)}
                  list={id("modelos")}
                  autoComplete="off"
                />
                <datalist id={id("modelos")}>
                  {KNOWN_GPS_MODELS.map((model) => (
                    <option key={model.code} value={model.code} />
                  ))}
                </datalist>
              </Field>
              <Field label="Protocolo" htmlFor={id("protocolo")} error={errors.protocolo}>
                <select
                  {...control("protocolo")}
                  value={state.protocolo}
                  onChange={(e) => set("protocolo", e.target.value as RastreoProtocol)}
                  disabled={protocolLocked}
                >
                  <option value="">Selecciona…</option>
                  {RASTREO_PROTOCOLS.map((protocol) => (
                    <option key={protocol} value={protocol}>
                      {PROTOCOL_LABEL[protocol]}
                    </option>
                  ))}
                </select>
              </Field>
              <p className="rounded-[14px] border border-gd-line bg-gd-mist px-4 py-3 text-[14px] text-gd-ink sm:col-span-2">
                {target.mode === "crear" && target.pendiente ? (
                  <>
                    Se registra el GPS que termina en{" "}
                    <strong className="font-semibold">{target.pendiente.identificadorFinal}</strong>, que ya está transmitiendo.
                    {target.pendiente.protocolo
                      ? " El IMEI y el protocolo salen de su trama."
                      : " El IMEI sale de su trama; su protocolo no se reconoció: elígelo o déjalo por confirmar."}{" "}
                    Compara los últimos 4 dígitos con la etiqueta del equipo antes de guardar: cualquiera que conozca el
                    servidor puede mandar un IMEI ajeno.
                  </>
                ) : target.mode === "crear" && target.motor ? (
                  <>
                    Se registra el equipo <strong className="font-semibold">{target.motor.name ?? "sin nombre"}</strong>, que ya
                    está reportando.
                    {source === "motor"
                      ? " El IMEI es el que el motor ya tiene de ese equipo."
                      : " Escribe su IMEI: si no es el de ese equipo, no se guarda nada."}
                  </>
                ) : editing ? (
                  "Si cambias el IMEI, el nombre, el modelo o el protocolo, el motor de rastreo se actualiza al guardar."
                ) : (
                  "Al guardar, el equipo queda registrado en el motor de rastreo con su IMEI: sus posiciones se ven apenas reporte."
                )}
              </p>
            </Section>

            <Section title="SIM">
              <Field label="Número de la línea" htmlFor={id("sim-numero")} error={errors["sim-numero"]}>
                <input
                  {...control("sim-numero")}
                  value={state.simNumero}
                  onChange={(e) => set("simNumero", e.target.value)}
                  inputMode="tel"
                  autoComplete="off"
                />
              </Field>
              <Field label="Operador" htmlFor={id("sim-operador")} error={errors["sim-operador"]}>
                <select {...control("sim-operador")} value={state.simOperador} onChange={(e) => set("simOperador", e.target.value as SimOperator)}>
                  <option value="">Selecciona…</option>
                  {SIM_OPERATORS.map((operator) => (
                    <option key={operator} value={operator}>
                      {SIM_OPERATOR_LABEL[operator]}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="ICCID (opcional)" htmlFor={id("sim-iccid")} hint="19 o 20 dígitos impresos en la SIM." error={errors["sim-iccid"]}>
                  <input
                    {...control("sim-iccid")}
                    value={state.simIccid}
                    onChange={(e) => set("simIccid", e.target.value)}
                    inputMode="numeric"
                    autoComplete="off"
                  />
                </Field>
              </div>
            </Section>

            <Section title="Vehículo">
              <Field label="Placa (opcional)" htmlFor={id("placa")} hint="Vacía si el equipo todavía no está instalado." error={errors.placa}>
                <input
                  {...control("placa")}
                  value={state.placa}
                  onChange={(e) => set("placa", e.target.value)}
                  autoCapitalize="characters"
                  autoComplete="off"
                />
              </Field>
              <Field label="Ciudad donde circula" htmlFor={id("ciudad")} error={errors.ciudad}>
                <input {...control("ciudad")} value={state.ciudad} onChange={(e) => set("ciudad", e.target.value)} list={id("ciudades")} autoComplete="off" />
              </Field>
              <Field label="Fecha de instalación" htmlFor={id("instalado")} error={errors.instalado}>
                <input {...control("instalado")} type="date" max={today} value={state.instaladoEn} onChange={(e) => set("instaladoEn", e.target.value)} />
              </Field>
              <Field label="Notas (opcional)" htmlFor={id("notas")} error={errors.notas}>
                <input {...control("notas")} value={state.notas} onChange={(e) => set("notas", e.target.value)} autoComplete="off" />
              </Field>
            </Section>

            <Section title="Cliente">
              <fieldset className="sm:col-span-2">
                <legend className="gd-label">¿De quién es el vehículo?</legend>
                <div className="grid gap-2 sm:grid-cols-3">
                  {(
                    [
                      ["nuevo", "Cliente nuevo"],
                      ["existente", "Cliente registrado"],
                      ["ninguno", "Sin cliente por ahora"],
                    ] as const
                  ).map(([value, label]) => (
                    <label
                      key={value}
                      className={`flex min-h-12 cursor-pointer items-center gap-2.5 rounded-[14px] border px-3 text-[15px] has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-gd-cyan ${
                        state.clienteModo === value ? "border-gd-cyan bg-white font-semibold text-gd-navy" : "border-gd-line-strong bg-white text-gd-ink"
                      }`}
                    >
                      <input
                        type="radio"
                        name={id("cliente-modo")}
                        value={value}
                        checked={state.clienteModo === value}
                        onChange={() =>
                          setState((current) => ({
                            ...current,
                            clienteModo: value,
                            corregirCliente: false,
                            cliente: value === "nuevo" ? EMPTY_CLIENTE : clienteState(clientes.find((item) => item.id === current.clienteId)),
                          }))
                        }
                        className="size-4 accent-[var(--color-gd-blue)]"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>

              {state.clienteModo === "existente" ? (
                <div className="space-y-3 sm:col-span-2">
                  <Field label="Cliente" htmlFor={id("cliente-id")} error={errors["cliente-id"]}>
                    <select {...control("cliente-id")} value={state.clienteId} onChange={(e) => chooseClient(e.target.value)}>
                      <option value="">{sortedClients.length ? "Selecciona…" : "Todavía no hay clientes registrados"}</option>
                      {sortedClients.map((cliente) => (
                        <option key={cliente.id} value={cliente.id}>
                          {cliente.nombre} · {cliente.tipoDocumento} {cliente.documento}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {state.clienteId ? (
                    <label className="flex items-center gap-2.5 text-[15px] text-gd-ink">
                      <input
                        type="checkbox"
                        checked={state.corregirCliente}
                        onChange={(e) => set("corregirCliente", e.target.checked)}
                        className="size-4 accent-[var(--color-gd-blue)]"
                      />
                      Corregir los datos de este cliente
                    </label>
                  ) : null}
                </div>
              ) : null}

              {clientFieldsVisible ? (
                <>
                  <div className="sm:col-span-2">
                    <Field label="Nombre o razón social" htmlFor={id("cliente-nombre")} error={errors["cliente-nombre"]}>
                      <input {...control("cliente-nombre")} value={state.cliente.nombre} onChange={(e) => setCliente("nombre", e.target.value)} autoComplete="off" />
                    </Field>
                  </div>
                  <Field label="Tipo de documento" htmlFor={id("cliente-tipo")} error={errors["cliente-tipo"]}>
                    <select
                      {...control("cliente-tipo")}
                      value={state.cliente.tipoDocumento}
                      onChange={(e) => setCliente("tipoDocumento", e.target.value as DocumentType)}
                    >
                      {DOCUMENT_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {DOCUMENT_TYPE_LABEL[type]}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Número de documento" htmlFor={id("cliente-documento")} error={errors["cliente-documento"]}>
                    <input {...control("cliente-documento")} value={state.cliente.documento} onChange={(e) => setCliente("documento", e.target.value)} autoComplete="off" />
                  </Field>
                  <Field label="Teléfono" htmlFor={id("cliente-telefono")} error={errors["cliente-telefono"]}>
                    <input
                      {...control("cliente-telefono")}
                      value={state.cliente.telefono}
                      onChange={(e) => setCliente("telefono", e.target.value)}
                      inputMode="tel"
                      autoComplete="off"
                    />
                  </Field>
                  <Field label="Ciudad" htmlFor={id("cliente-ciudad")} error={errors["cliente-ciudad"]}>
                    <input
                      {...control("cliente-ciudad")}
                      value={state.cliente.ciudad}
                      onChange={(e) => setCliente("ciudad", e.target.value)}
                      list={id("ciudades")}
                      autoComplete="off"
                    />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Dirección" htmlFor={id("cliente-direccion")} error={errors["cliente-direccion"]}>
                      <input {...control("cliente-direccion")} value={state.cliente.direccion} onChange={(e) => setCliente("direccion", e.target.value)} autoComplete="off" />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field
                      label="Póliza (opcional)"
                      htmlFor={id("cliente-poliza")}
                      hint="Vacía si el cliente solo tiene GPS con nosotros."
                      error={errors["cliente-poliza"]}
                    >
                      <input {...control("cliente-poliza")} value={state.cliente.poliza} onChange={(e) => setCliente("poliza", e.target.value)} autoComplete="off" />
                    </Field>
                  </div>
                </>
              ) : null}
            </Section>

            <datalist id={id("ciudades")}>
              {SUGGESTED_CITIES.map((city) => (
                <option key={city} value={city} />
              ))}
            </datalist>
          </div>

          <footer className="flex flex-col-reverse gap-2 border-t border-gd-line bg-white px-4 py-3 sm:flex-row sm:justify-end sm:px-6">
            <button type="button" onClick={onCancel} disabled={mutation.isPending} className="gd-btn-secondary">
              Cancelar
            </button>
            <button type="submit" disabled={mutation.isPending} className="gd-btn-primary">
              {mutation.isPending ? "Guardando…" : editing ? "Guardar cambios" : "Guardar dispositivo"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="gd-card m-0 min-w-0 p-4 sm:p-5">
      <legend className="float-left mb-3 w-full font-display text-[13px] font-bold tracking-wide text-gd-blue uppercase">
        {title}
      </legend>
      <div className="clear-both grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}
